import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import SEO, { breadcrumbSchema } from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import LeadForm from "@/components/LeadForm";
import { Loader2, MapPin, CheckCircle2, Quote, ArrowRight, Award, Calendar, GraduationCap } from "lucide-react";
import { COMPANY, nearestBranchForState } from "@/data/branches";
import { PRODUCT_IMG } from "@/data/siteMedia";
import { renderMarkdown } from "@/lib/markdown";
import { cityHref, stateHref } from "@/lib/urls";

// Default narrative blocks used when an installation doesn't yet have a custom
// admin-curated story. These are generic but keyword-rich + interpolated with
// real customer/product/city data so each page is still unique enough for SEO.
function buildDefaultStory(ins) {
  const teacher = ins.coaching_name && ins.coaching_name.toLowerCase() !== "na"
    ? ins.coaching_name
    : (ins.customer || "the educator");
  const subject = ins.teacher_subject || "their core subject";
  const product = ins.product && ins.product.toLowerCase() !== "nan"
    ? ins.product
    : "Smart Infovision Digital Board";
  const size = ins.size && ins.size !== "NA" && ins.size !== "nan" ? `${ins.size}"` : "";
  const city = ins.city || "their city";

  return [
    {
      heading: `How ${teacher} connected with Smart Infovision`,
      body: `${teacher} reached out to Smart Infovision while exploring Interactive Flat Panel options in ${city}. After comparing several Digital Board brands, ${teacher} chose Smart Infovision for the transparent pricing, the same-day on-site demo and the 3-year warranty bundled with every installation. The decision started with a single WhatsApp message to ${COMPANY.sales_phone} and within hours our local team was on a call to understand the requirement, classroom dimensions and student count.`,
    },
    {
      heading: "The classroom challenge before the upgrade",
      body: `Like most ambitious educators, ${teacher} was juggling chalkboards, photocopies and projector cables — costing precious teaching minutes every period. Students at the back rows often missed details on a small whiteboard, and there was no easy way to record lessons for absentees or for hybrid / online batches. Online platform integration, screen-sharing from a phone or laptop and saving annotated content for revision were the top three pain points.`,
    },
    {
      heading: `How the ${product}${size ? ` ${size}` : ""} solved the problem`,
      body: `Smart Infovision installed a ${product}${size ? ` ${size}` : ""} along with wall mount, network setup and a one-day teacher training. With 4K resolution, 20-point touch and an Android 14 OS, ${teacher} could now annotate over any PDF or YouTube video, wirelessly cast from a phone, and save every lesson to a personal cloud library. The built-in NCERT / ICSE content library shaved off hours of preparation time every week — and the in-built speakers + dedicated teacher mic meant even the last bench of a 50-student room hears every word.`,
    },
    {
      heading: "After Smart Infovision: a new way to teach online + offline",
      body: `Within the first month, ${teacher} expanded teaching reach by going hybrid — running offline batches in ${city} while live-streaming the same class to outstation students through the Digital Board's built-in camera and OBS-ready output. ${subject.charAt(0).toUpperCase() + subject.slice(1)} concepts that earlier needed three days of revision now wrap up in a single recorded session that students replay on demand. Smart Infovision's pan-India service team handles AMC, software updates and content-library refreshes — so ${teacher} can stay focused on teaching, not tech.`,
    },
    {
      heading: "Why educators in your city pick Smart Infovision",
      body: `10,000+ schools, coaching institutes and individual teachers across India trust Smart Infovision for the same three reasons ${teacher} chose us — direct-from-manufacturer pricing, a real local service team and a track record of 10,000+ verified installations across 30 Indian states. Visit our nearest Experience Center, request an on-site demo, or chat on WhatsApp to get a free quote that includes installation, training and 3-year warranty.`,
    },
  ];
}

function reviewSchema(ins) {
  const teacher = ins.coaching_name && ins.coaching_name.toLowerCase() !== "na"
    ? ins.coaching_name
    : ins.customer;
  if (!teacher) return null;
  return {
    "@context": "https://schema.org",
    "@type": "Review",
    itemReviewed: {
      "@type": "Product",
      name: ins.product || "Smart Infovision Digital Board",
      brand: { "@type": "Brand", name: "Smart Infovision" },
    },
    reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
    author: { "@type": "Person", name: teacher },
    reviewBody:
      ins.testimonial ||
      `Excellent installation by Smart Infovision in ${ins.city}. The ${ins.product || "Interactive Flat Panel"} has transformed our teaching workflow.`,
  };
}

export default function InstallationDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/installations/${id}`)
      .then((r) => setData(r.data))
      .catch(() => setData(false))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-[50vh] grid place-items-center">
        <Loader2 className="animate-spin text-slate-900" />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="si-container py-20 text-center">
        <h1 className="text-2xl font-bold">Installation not found</h1>
        <Link to="/" className="text-slate-900 underline mt-4 inline-block">Back home</Link>
      </div>
    );
  }

  const ins = data.installation;
  const similar = data.similar || [];
  const teacher = ins.coaching_name && ins.coaching_name.toLowerCase() !== "na"
    ? ins.coaching_name
    : ins.customer || ins.siv || "Verified Customer";
  const subject = ins.teacher_subject || "";
  const role = ins.teacher_role || "";
  const product = ins.product && ins.product.toLowerCase() !== "nan" ? ins.product : "Digital Board";
  const sizeLabel = ins.size && ins.size !== "NA" && ins.size !== "nan" ? `${ins.size}"` : "";
  const city = ins.city || "";
  const state = ins.state || "";
  const nearest = nearestBranchForState(state);
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  // SEO copy — uses keyword-rich title + meta + structured headings.
  const title = ins.story_title
    ? `${ins.story_title} | Smart Infovision`
    : `${teacher}${city ? ` · ${city}` : ""} — ${product}${sizeLabel ? ` ${sizeLabel}` : ""} Installation by Smart Infovision`;
  const metaDesc = ins.story_meta_description
    ? ins.story_meta_description.slice(0, 160)
    : `Read how ${teacher} in ${city}, ${state} upgraded to a ${product}${sizeLabel ? ` ${sizeLabel}` : ""} with Smart Infovision — the journey, the classroom challenges and the online-teaching wins.`.slice(0, 160);

  // If admin has written a markdown story, render it as a single rich block.
  // Otherwise fall back to the 5-section default template.
  const useMarkdown = !!(ins.story && ins.story.includes("##"));
  const sections = ins.story && !useMarkdown
    ? [{ heading: ins.story_title || `${teacher}'s journey with Smart Infovision`, body: ins.story }]
    : buildDefaultStory(ins);

  const rs = reviewSchema(ins);
  const breadcrumbs = [
    state && { label: state, to: stateHref(ins.state_slug) },
    city && { label: city, to: cityHref(ins.city_slug) },
    { label: teacher },
  ].filter(Boolean);

  return (
    <>
      <SEO
        title={title}
        description={metaDesc}
        canonical={`${origin}/installations/${ins.id}`}
        image={ins.photo_url || PRODUCT_IMG.panel65to86}
        schemas={[
          breadcrumbSchema([
            { label: "Home", url: origin + "/" },
            state && { label: state, url: `${origin}${stateHref(ins.state_slug)}` },
            city && { label: city, url: `${origin}${cityHref(ins.city_slug)}` },
            { label: teacher, url: `${origin}/installations/${ins.id}` },
          ].filter(Boolean)),
          rs,
        ].filter(Boolean)}
      />
      <section className="bg-black text-white py-12">
        <div className="si-container grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <Breadcrumbs items={breadcrumbs} dark />
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {ins.siv && (
                <span className="text-[10px] uppercase tracking-[0.2em] font-bold px-2.5 py-1 rounded bg-white/15 border border-white/20">
                  {ins.siv}
                </span>
              )}
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold px-2.5 py-1 rounded bg-[#EA580C] border border-[#EA580C]">
                Verified Customer
              </span>
            </div>
            <h1 className="mt-3 text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]" style={{ fontFamily: "Outfit" }}>
              {teacher}
              <br />
              <span className="text-white/70 text-2xl md:text-3xl lg:text-4xl font-bold">
                {product}{sizeLabel ? ` ${sizeLabel}` : ""} installation in {city}
              </span>
            </h1>
            <p className="mt-4 text-white/70 text-base md:text-lg max-w-2xl">
              How {teacher} partnered with Smart Infovision to upgrade to {product}{sizeLabel ? ` ${sizeLabel}` : ""} and transform their {role || "classroom"} into a smart, hybrid teaching setup.
            </p>
            <div className="mt-6 flex flex-wrap gap-2 text-xs">
              {city && (
                <Link to={cityHref(ins.city_slug)} className="px-3 py-1.5 rounded-full bg-white/10 border border-white/20 hover:bg-white/20">
                  <MapPin size={11} className="inline mr-1" /> {city}
                </Link>
              )}
              {state && (
                <Link to={stateHref(ins.state_slug)} className="px-3 py-1.5 rounded-full bg-white/10 border border-white/20 hover:bg-white/20">
                  {state}
                </Link>
              )}
              {product && product.toLowerCase() !== "nan" && (
                <span className="px-3 py-1.5 rounded-full bg-white/10 border border-white/20">
                  <Award size={11} className="inline mr-1" /> {product}
                </span>
              )}
              {sizeLabel && (
                <span className="px-3 py-1.5 rounded-full bg-[#EA580C]/30 border border-[#EA580C]/60">
                  {sizeLabel}
                </span>
              )}
              {subject && (
                <span className="px-3 py-1.5 rounded-full bg-white/10 border border-white/20">
                  <GraduationCap size={11} className="inline mr-1" /> {subject}
                </span>
              )}
            </div>
          </div>
          <div className="lg:col-span-5 lg:sticky lg:top-24">
            {ins.photo_url ? (
              <img src={ins.photo_url} alt={`${teacher} · ${product} installation in ${city}`} className="rounded-xl shadow-xl w-full h-auto" loading="lazy" />
            ) : (
              <img src={PRODUCT_IMG.panel65to86} alt={`${product} installation in ${city}`} className="rounded-xl shadow-xl w-full h-auto" loading="lazy" />
            )}
          </div>
        </div>
      </section>

      {ins.testimonial && (
        <section className="py-10 bg-orange-50/60 border-y border-slate-200">
          <div className="si-container max-w-3xl">
            <Quote size={28} className="text-[#EA580C]" />
            <p className="mt-3 text-xl md:text-2xl font-semibold leading-relaxed text-slate-900" style={{ fontFamily: "Outfit" }}>
              "{ins.testimonial}"
            </p>
            <p className="mt-3 text-sm text-slate-600">— {ins.testimonial_author || teacher}, {city}</p>
          </div>
        </section>
      )}

      <section className="py-14 si-container grid lg:grid-cols-3 gap-10">
        <article className="lg:col-span-2 space-y-10 prose-slate" data-testid="installation-story">
          {useMarkdown ? (
            <div className="prose-si max-w-none" dangerouslySetInnerHTML={{ __html: renderMarkdown(ins.story) }} />
          ) : (
            sections.map((s, i) => (
              <div key={i}>
                <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-3" style={{ fontFamily: "Outfit" }}>
                  {s.heading}
                </h2>
                <p className="text-slate-700 leading-relaxed whitespace-pre-line">{s.body}</p>
              </div>
            ))
          )}

          <div className="grid sm:grid-cols-2 gap-3 pt-2">
            {[
              "Free on-site Digital Board demo in " + (city || "your city"),
              "Direct-from-manufacturer pricing — no middlemen",
              "3-year on-site warranty + AMC available",
              "Pan-India installation in 700+ cities",
              "Free teacher training and content library setup",
              "Same-day support — WhatsApp " + COMPANY.sales_phone,
            ].map((b, i) => (
              <div key={i} className="flex items-start gap-2.5 p-3.5 rounded-lg border border-slate-200 bg-slate-50">
                <CheckCircle2 size={18} className="text-emerald-500 mt-0.5 shrink-0" />
                <span className="text-sm font-medium text-slate-800">{b}</span>
              </div>
            ))}
          </div>

          {nearest?.primary && (
            <div className="rounded-xl border border-slate-200 bg-white p-5 flex flex-wrap items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-slate-900/10 text-slate-900 grid place-items-center shrink-0">
                <MapPin size={20} />
              </div>
              <div className="flex-1 min-w-[240px]">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-900">Nearest Experience Center</div>
                <div className="font-semibold text-lg mt-0.5" style={{ fontFamily: "Outfit" }}>
                  {nearest.primary.name}
                </div>
                <p className="text-sm text-slate-600 mt-1">{nearest.primary.address}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <a href={nearest.primary.map} target="_blank" rel="noreferrer" className="text-xs px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-900 font-semibold">
                    Open in Maps
                  </a>
                  <Link to={`/contact?action=demo&center=${nearest.primary.slug}`} className="text-xs px-3 py-1.5 rounded-md bg-[#EA580C] text-white hover:bg-[#C2410C] font-semibold">
                    Book a Visit
                  </Link>
                </div>
              </div>
            </div>
          )}
        </article>
        <aside>
          <div className="lg:sticky lg:top-24">
            <LeadForm
              sourcePage={`installation_${ins.id}`}
              defaultCity={city}
              defaultState={state}
              defaultInterest={product}
              title={`Want a similar setup in ${city || "your school"}?`}
              subtitle="Talk to the same team that handled this installation."
              testIdPrefix="installation-lead-form"
            />
          </div>
        </aside>
      </section>

      {similar.length > 0 && (
        <section className="py-14 bg-slate-50 border-t border-slate-200">
          <div className="si-container">
            <span className="text-xs uppercase tracking-[0.2em] text-[#EA580C] font-bold">More from {city}</span>
            <h2 className="mt-2 text-2xl md:text-3xl font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>
              Other Smart Infovision installations in {city}
            </h2>
            <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="installation-similar-grid">
              {similar.map((s) => (
                <Link key={s.id} to={`/installations/${s.id}`} className="group p-4 rounded-lg border border-slate-200 bg-white hover:border-slate-900 hover:shadow-md transition-all" data-testid={`similar-install-${s.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-bold leading-snug min-w-0 group-hover:text-slate-900">
                      {s.coaching_name && s.coaching_name.toLowerCase() !== "na"
                        ? s.coaching_name
                        : (s.customer || s.siv)}
                    </div>
                    {s.siv && <span className="text-[10px] px-2 py-1 rounded bg-slate-50 text-slate-900 font-bold shrink-0">{s.siv}</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{s.city}</div>
                  {(s.product && s.product.toLowerCase() !== "nan") && (
                    <div className="mt-2.5 text-xs flex flex-wrap gap-1.5">
                      <span className="px-2 py-1 rounded bg-slate-100 text-slate-700 font-semibold">{s.product}</span>
                      {s.size && s.size !== "NA" && s.size !== "nan" && (
                        <span className="px-2 py-1 rounded bg-orange-50 text-[#EA580C] font-semibold">{s.size}"</span>
                      )}
                    </div>
                  )}
                  <div className="mt-3 text-xs text-slate-900 font-semibold opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1">
                    Read story <ArrowRight size={11} />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
